import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Youtube, Facebook, Phone, Mail, MapPin, Send, Loader2, MessageCircle } from "lucide-react";
import { api, cachedGet, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Logo } from "@/components/Nav";

const CONTACT_PHONE = "917013095204";

export default function Footer({ scrollTo }) {
  const [photos, setPhotos] = useState([]);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    cachedGet("/gallery").then(({ data }) => setPhotos(data)).catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", { name: "Newsletter Subscriber", email, phone: "-", event_type: "Newsletter Signup", city: "", event_date: "", package: "", message: "" });
      toast.success("Subscribed — see you at the next sandhya!");
      setEmail("");
    } catch {
      toast.error("Could not subscribe — try again.");
    }
    setSending(false);
  };

  const go = (id) => scrollTo && scrollTo(id);

  return (
    <footer className="border-t border-bone/10 bg-surface/40" data-testid="site-footer">
      <div className="px-6 md:px-12 pt-16 pb-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10">
        <div>
          <Logo />
          <p className="mt-5 text-xs leading-relaxed text-ash">
            We are on a mission to spread devotion through music and create a community bound by love, faith and harmony.
          </p>
          <div className="mt-5 flex gap-3">
            <a href="https://www.instagram.com/thebandsumiran/" target="_blank" rel="noreferrer" aria-label="Instagram" className="w-9 h-9 rounded-full border border-bone/20 flex items-center justify-center text-bone/70 hover:text-saffron hover:border-saffron transition-colors" data-testid="footer-social-instagram"><Instagram size={15} /></a>
            <a href="https://www.youtube.com" target="_blank" rel="noreferrer" aria-label="YouTube" className="w-9 h-9 rounded-full border border-bone/20 flex items-center justify-center text-bone/70 hover:text-saffron hover:border-saffron transition-colors" data-testid="footer-social-youtube"><Youtube size={15} /></a>
            <a href="https://www.facebook.com" target="_blank" rel="noreferrer" aria-label="Facebook" className="w-9 h-9 rounded-full border border-bone/20 flex items-center justify-center text-bone/70 hover:text-saffron hover:border-saffron transition-colors" data-testid="footer-social-facebook"><Facebook size={15} /></a>
            <a href={`https://wa.me/${CONTACT_PHONE}`} target="_blank" rel="noreferrer" aria-label="WhatsApp" className="w-9 h-9 rounded-full border border-bone/20 flex items-center justify-center text-bone/70 hover:text-saffron hover:border-saffron transition-colors" data-testid="footer-social-whatsapp"><MessageCircle size={15} /></a>
          </div>
        </div>

        <div>
          <p className="font-display text-[11px] tracking-[0.25em] text-gold">QUICK LINKS</p>
          <div className="mt-5 flex flex-col gap-2.5 text-xs text-bone/70">
            <Link to="/" className="hover:text-saffron transition-colors w-fit" data-testid="footer-link-home">Home</Link>
            <Link to="/upcomingshows" className="hover:text-saffron transition-colors w-fit" data-testid="footer-link-events">Events</Link>
            <button onClick={() => go("#watch")} className="text-left hover:text-saffron transition-colors w-fit" data-testid="footer-link-videos">Videos</button>
            <button onClick={() => go("#glimpses")} className="text-left hover:text-saffron transition-colors w-fit" data-testid="footer-link-gallery">Gallery</button>
            <button onClick={() => go("#about")} className="text-left hover:text-saffron transition-colors w-fit" data-testid="footer-link-about">About Us</button>
            <Link to="/bookprivateshow" className="hover:text-saffron transition-colors w-fit" data-testid="footer-link-bookings">Bookings</Link>
            <Link to="/contact" className="hover:text-saffron transition-colors w-fit" data-testid="footer-link-contact">Contact Us</Link>
          </div>
        </div>

        <div>
          <p className="font-display text-[11px] tracking-[0.25em] text-gold">FOR EVENTS & BOOKINGS</p>
          <div className="mt-5 space-y-3 text-xs text-bone/70">
            <a href={`tel:+${CONTACT_PHONE}`} className="flex items-center gap-2.5 hover:text-saffron transition-colors" data-testid="footer-phone"><Phone size={13} className="text-saffron" /> +91 70130 95204</a>
            <a href="mailto:bookings@thebandsumiran.com" className="flex items-center gap-2.5 hover:text-saffron transition-colors" data-testid="footer-email"><Mail size={13} className="text-saffron" /> bookings@thebandsumiran.com</a>
            <p className="flex items-center gap-2.5"><MapPin size={13} className="text-saffron" /> Hyderabad, India</p>
            <a href={`https://wa.me/${CONTACT_PHONE}`} target="_blank" rel="noreferrer"
              className="mt-2 inline-flex items-center gap-2 px-6 py-3 border border-saffron/50 text-saffron text-[10px] font-bold tracking-[0.15em] rounded-full hover:bg-saffron hover:text-ink transition-colors"
              data-testid="footer-whatsapp-btn">
              <MessageCircle size={14} /> WHATSAPP US
            </a>
          </div>
        </div>

        <div>
          <p className="font-display text-[11px] tracking-[0.25em] text-gold">NEWSLETTER</p>
          <p className="mt-5 text-xs text-ash leading-relaxed">Stay updated with our latest events and devotional vibes.</p>
          <form onSubmit={subscribe} className="mt-4 flex" data-testid="newsletter-form">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email"
              className="flex-1 min-w-0 bg-ink border border-bone/15 rounded-l-full px-4 py-3 text-xs text-bone placeholder:text-ash/60 focus:border-saffron focus:outline-none transition-colors"
              data-testid="newsletter-email-input" />
            <button type="submit" disabled={sending}
              className="px-5 bg-saffron text-ink rounded-r-full hover:bg-gold transition-colors disabled:opacity-50 flex items-center"
              data-testid="newsletter-submit-btn" aria-label="Subscribe">
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            </button>
          </form>
        </div>

        <div>
          <p className="font-display text-[11px] tracking-[0.25em] text-gold">FOLLOW US</p>
          <div className="mt-5 grid grid-cols-3 gap-2">
            {photos.slice(0, 6).map((p, i) => (
              <a key={p.id} href="https://www.instagram.com/thebandsumiran/" target="_blank" rel="noreferrer"
                className="relative rounded-md overflow-hidden aspect-square group" data-testid={`footer-ig-${i}`}>
                <img src={fileUrl(p.image_url)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <Instagram size={11} className="absolute top-1 right-1 text-bone/80" />
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-bone/10 px-6 md:px-12 py-5 flex flex-wrap items-center justify-between gap-4">
        <p className="text-[11px] text-ash">© {new Date().getFullYear()} The Band Sumiran. All Rights Reserved.</p>
        <div className="flex items-center gap-5 text-[11px] text-ash">
          <Link to="/privacy" className="hover:text-saffron transition-colors" data-testid="footer-link-privacy">Privacy Policy</Link>
          <span className="text-bone/20">|</span>
          <Link to="/terms" className="hover:text-saffron transition-colors" data-testid="footer-link-terms">Terms & Conditions</Link>
          <span className="text-bone/20">|</span>
          <Link to="/refund" className="hover:text-saffron transition-colors" data-testid="footer-link-refund">Refund Policy</Link>
          <span className="text-bone/20">|</span>
          <Link to="/admin" className="hover:text-saffron transition-colors" data-testid="footer-admin-link">Band Login</Link>
        </div>
      </div>
    </footer>
  );
}
